import { Controller } from "@hotwired/stimulus"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { CssHelper } from "~/helpers/css_helper"
import { Chart, registerables } from "chart.js"
import ChartjsPluginWatermark from "chartjs-plugin-watermark"

Chart.register(...registerables)
Chart.register(ChartjsPluginWatermark)

export default class extends Controller {
  static get targets() {
    return ["chartCanvas"]
  }

  static get values() {
    return {
      rate: { type: Boolean, default: false },
      data: Array,
      labels: Array,
      title: String,
      borderColor: String,
      tooltipLabel: String,
      watermark: String,
    }
  }

  connect() {
    const _this = this
    const chartEl = this.chartCanvasTarget.getContext("2d")
    const numberSeparator = i18n.translations[i18n.locale].js.number.format.separator
    const numberDelimiter = i18n.translations[i18n.locale].js.number.format.delimiter
    const fontFamily = CssHelper.getVar("--fonts-family-body", document.querySelector(".application-back"))

    this.gradient = chartEl.createLinearGradient(0, 0, 0, 400)
    this.gradient.addColorStop(0, this._lightenColor(this.borderColorValue, 0.8))
    this.gradient.addColorStop(1, this._lightenColor(this.borderColorValue, 0.95))

    this.config = {
      type: "line",
      data: {
        labels: this.labelsValue,
        datasets: [
          {
            label: this.titleValue,
            data: this.dataValue,
            fill: true,
            backgroundColor: this.gradient,
            borderColor: this.borderColorValue,
            tension: 0.4,
          },
        ],
      },
      options: {
        animation: false, // See https://github.com/chartjs/Chart.js/issues/11310 for why this is needed
        font: {
          family: fontFamily,
          size: 10,
          color: CssHelper.getVar("--colors-grey-600"),
        },
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
              callback: function (value, _index, _values) {
                let tick = value

                if (_this.rateValue) {
                  tick = `${tick}%`
                } else {
                  tick = CurrencyHelper.numberWithDelimiter(value, numberDelimiter, numberSeparator)
                }

                return tick
              },
            },
          },
        },
        elements: {
          point: {
            radius: 4,
            backgroundColor: "rgb(230,252,229)",
            borderWidth: 2,
            borderColor: this.borderColorValue,
            hoverBorderWidth: 2,
            hoverRadius: 6,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: function (tooltipItem) {
                let value = tooltipItem.raw

                if (_this.rateValue) {
                  value = `${value}%`
                } else {
                  value = CurrencyHelper.numberWithDelimiter(value, numberDelimiter, numberSeparator)
                }

                return `${_this.tooltipLabelValue} ${value}`
              },
            },
            displayColors: false,
            padding: 12,
            titleFont: {
              family: fontFamily,
              size: 12,
            },
            titleMarginBottom: 4,
            bodyFont: {
              family: fontFamily,
              size: 12,
            },
            cornerRadius: 4,
            backgroundColor: "rgba(29, 38, 16, .95)",
            titleColor: "#FFFFFF",
            bodyColor: CssHelper.getVar("--colors-grey-200"),
            bodySpacing: 4,
          },
        },
      },
    }

    Chart.defaults.borderColor = CssHelper.getVar("--colors-grey-200")
    new Chart(chartEl, this.config)
  }

  export(e) {
    const exportCanvasContainer = document.createElement("div")
    const exportCanvas = document.createElement("canvas")
    const downloadLink = document.createElement("a")
    const button = e.currentTarget
    const exportName = button.dataset.name
    const originalCta = button.innerHTML
    const fontFamily = CssHelper.getVar("--fonts-family-body", document.querySelector(".application-back"))
    let hasWatermark = false
    let exportConfig = JSON.parse(JSON.stringify(this.config))

    // Reset gradient
    // without resetting the background is black in the export.
    exportConfig.data.datasets[0].backgroundColor = this.gradient

    // Chart.js plugin to add a custom background
    const plugin = {
      id: "customCanvasBackground",
      beforeDraw: (chart) => {
        const { ctx } = chart
        ctx.save()
        ctx.globalCompositeOperation = "destination-over"

        // Adds a white rounded background and simulate a "padding"
        ctx.fillStyle = "#ffffff"
        ctx.roundRect(16, 16, chart.width - 32, chart.height - 32, 8)
        ctx.fill()

        // Adds a orange background
        ctx.fillStyle = CssHelper.getVar("--colors-primary-600")
        ctx.fillRect(0, 0, chart.width, chart.height)
        ctx.restore()
      },
    }

    exportCanvasContainer.classList.add("export-canvas-container")
    exportCanvas.classList.add("export-canvas-placeholder")
    exportCanvas.id = "exportCanvas"

    exportCanvasContainer.appendChild(exportCanvas)
    this.element.appendChild(exportCanvasContainer)

    // Manage Chart padding
    exportConfig.options.layout = {
      padding: {
        top: 40,
        right: 56,
        bottom: 40,
        left: 56,
      },
    }

    // Text (title and subtitle)
    exportConfig.options.plugins = {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: this.tooltipLabelValue.replace(":", ""),
        align: "start",
        color: CssHelper.getVar("--colors-grey-900"),
        font: {
          size: 18,
          family: fontFamily,
          weight: 500,
        },
        padding: {
          top: 0,
          bottom: 4,
        },
      },
      subtitle: {
        display: true,
        text: i18n.t("js.back.campaigns.reporting.graphs.export_date", {
          start_date: e.params.startDate,
          end_date: e.params.endDate,
        }),
        align: "start",
        color: CssHelper.getVar("--colors-grey-700"),
        font: {
          size: 13,
          family: fontFamily,
          weight: 500,
        },
        padding: {
          bottom: 32,
        },
      },
    }

    exportConfig.options.watermark = {
      image: this.watermarkValue,
      width: 110,
      height: 20,
      x: 48,
      y: 48,
      opacity: 1,
      alignX: "right",
      alignY: "top",
      alignToChartArea: false,
      position: "front",
    }

    exportConfig.options.animation = {
      duration: 0,
      onComplete: function (e) {
        // onComplete is triggered two times:
        // 1. On original rendering
        // 2. When the watermark is added
        // we only want to download the version with the watermark:
        if (hasWatermark) {
          downloadLink.href = exportCanvas.toDataURL()
          downloadLink.download = exportName
          downloadLink.click()
          exportCanvasContainer.remove()

          setTimeout(() => {
            button.innerHTML = originalCta
          }, 500)

          exportConfig.options.animation.onComplete = null
        }

        hasWatermark = true
      },
    }

    exportConfig.options.maintainAspectRatio = true

    // Add background color using a custom plugin
    exportConfig.plugins = [plugin]
    exportConfig.options.plugins.customCanvasBackground

    new Chart(exportCanvas.getContext("2d"), exportConfig)
  }

  _lightenColor(hex, percent) {
    // Ensure the percent is between 0 and 1
    if (percent < 0) {
      percent = 0
    } else if (percent > 1) {
      percent = 1
    }

    // Convert hex to RGB
    let r = parseInt(hex.substring(1, 3), 16)
    let g = parseInt(hex.substring(3, 5), 16)
    let b = parseInt(hex.substring(5, 7), 16)

    // Calculate the adjustment value for each color
    let adjust = (amount, color) => {
      return Math.round((255 - color) * amount) + color
    }

    // Adjust and convert back to hex
    r = adjust(percent, r).toString(16).padStart(2, "0")
    g = adjust(percent, g).toString(16).padStart(2, "0")
    b = adjust(percent, b).toString(16).padStart(2, "0")

    return `#${r}${g}${b}`
  }
}
